import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
// @mui
import { Chip, Link } from '@mui/material';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import CourseGroupDialog from './course-group-new-dialog';

// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

// ----------------------------------------------------------------------

export default function CourseTableRow({
  row,
  selected,
  onEditRow,
  onSelectRow,
  onDeleteRow,
  onSaveSuccess,
}) {
  const {
    id,
    name,
    target_course,
    courses,
    last_updated,
    created_at,
  } = row;

  const quickEdit = useBoolean();
  const quickDelete = useBoolean();
  const popover = usePopover();

  const router = useRouter();

  const handleProfile = useCallback(
    (ids) => {
      router.push(paths.dashboard.courseGroup.coursegroupprofile(ids));
    },
    [router]
  );

  return (
    <>
      <TableRow hover selected={selected}>
        <TableCell padding="checkbox">
          <Checkbox checked={selected} onClick={onSelectRow} />
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Link
            noWrap
            variant="body2"
            onClick={() => handleProfile(id)}
            sx={{ color: 'black', typography: 'body2', cursor: 'pointer' }}
          >
            {name}
          </Link>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>{target_course?.course_name}</TableCell>

        <TableCell>
          {courses?.map((target, index) => (
            <Chip
              key={index}
              label={target.course_name}
              variant="soft"
              color="warning"
              sx={{
                m: '2px',
              }}
            />
          ))}
        </TableCell>

        <TableCell>
          <ListItemText
            primary={last_updated ? format(new Date(last_updated), 'dd/MM/yyyy') : '-'}
            secondary={last_updated ? format(new Date(last_updated), 'p') : '-'}
            primaryTypographyProps={{ typography: 'body2', noWrap: true }}
            secondaryTypographyProps={{
              mt: 0.5,
              component: 'span',
              typography: 'caption',
            }}
          />
        </TableCell>

        <TableCell>
          <ListItemText
            primary={created_at ? format(new Date(created_at), 'dd/MM/yyyy') : '-'}
            secondary={created_at ? format(new Date(created_at), 'p') : '-'}
            primaryTypographyProps={{ typography: 'body2', noWrap: true }}
            secondaryTypographyProps={{
              mt: 0.5,
              component: 'span',
              typography: 'caption',
            }}
          />
        </TableCell>

        <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
          <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <CourseGroupDialog
        currentID={id}
        open={quickEdit.value}
        onClose={quickEdit.onFalse}
        deleteMode={false}
        onSaveSuccess={onSaveSuccess}
      />
      <CourseGroupDialog
        currentID={id}
        open={quickDelete.value}
        onClose={quickDelete.onFalse}
        deleteMode={true}
        onDeleteSuccess={onDeleteRow}
      />

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        <MenuItem
          onClick={() => {
            quickDelete.onTrue();
            popover.onClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          Delete
        </MenuItem>

        <MenuItem
          onClick={() => {
            quickEdit.onTrue();
            popover.onClose();
          }}
        >
          <Iconify icon="solar:pen-bold" />
          Edit
        </MenuItem>
      </CustomPopover>
    </>
  );
}

CourseTableRow.propTypes = {
  onDeleteRow: PropTypes.func,
  onEditRow: PropTypes.func,
  onSaveSuccess: PropTypes.func,
  onSelectRow: PropTypes.func,
  row: PropTypes.object,
  selected: PropTypes.bool,
};